import { SchemaService } from "./schema.service";
import { Component, OnInit, ViewChild, ElementRef } from "@angular/core";
import { GenAiService, ChatMessage } from "./genai.service";
import { TemplateService } from "./template.service";

@Component({
  selector: "qp-root",
  templateUrl: "./app.component.html",
  styleUrls: ["./app.component.scss"],
})
export class AppComponent implements OnInit {
  title = "Question Playground";
  editorOptions = {
    theme: "vs-dark",
    language: "json",
    automaticLayout: true,
    formatOnPaste: true,
    formatOnType: true,
  } as const;
  schemaJson = "{}";

  schemaTree: SchemaTreeNode[] = [];
  private fullSchemaObj: any = null; // keep original loaded schema for tree building

  private editorRef: any;
  loadingGenAi = false;
  // Chat messages for conversational interface
  chatMessages: ChatMessage[] = [];
  chatInput = "";
  sendingChat = false;
  systemInstructions: string = "";
  showSystemInstructions = false; // collapsed by default
  loadingSchema = false;
  loadSchemaError = "";
  pathQuery = "";
  pathValid = false;
  pathError = "";
  // Autocomplete state for path input
  propertyPaths: string[] = [];
  filteredPaths: string[] = [];
  showPathSuggestions = false;
  activeSuggestionIndex = -1;
  readonly TOP_OPTION = "<Top>";
  developerMessageSent = false;

  // State for selected schema node (null means root)
  selectedNode: SchemaTreeNode | null = null;

  @ViewChild("chatInputRef") chatInputEl?: ElementRef<HTMLInputElement>;

  constructor(
    private genAi: GenAiService,
    private schemaService: SchemaService,
    private templateService: TemplateService, // inject template service
  ) {}

  private focusChatInput() {
    // Delay to ensure Angular has updated DOM state
    setTimeout(() => {
      try {
        this.chatInputEl?.nativeElement?.focus();
      } catch {}
    }, 0);
  }

  async clearSession() {
    if (this.sendingChat) return;
    this.chatMessages = [];
    this.chatInput = "";
    this.developerMessageSent = false;
    await this.startSessionForPath(this.pathQuery?.trim());
    this.focusChatInput();
  }

  async askPath() {
    const raw = this.pathQuery?.trim();
    if (!raw) return;
    if (this.sendingChat) return;
    this.chatMessages = [];
    this.chatInput = "";
    this.developerMessageSent = false;
    await this.startSessionForPath(raw);
    this.focusChatInput();
  }

  private async startSessionForPath(relativePath: string) {
    const isTop = relativePath === this.TOP_OPTION;
    if (!isTop) {
      this.validatePathOnChange();
      if (!this.pathValid) return;
    }
    const nodePrefix = this.selectedNode?.path || "";
    const absolutePath = isTop
      ? nodePrefix || ""
      : nodePrefix
        ? `${nodePrefix}.${relativePath}`
        : relativePath;
    const pathParts = absolutePath ? absolutePath.split(".") : [];
    const targetSchema = this.selectedNode
      ? this.selectedNode.schema
      : this.fullSchemaObj;
    const schemaSnippet = targetSchema
      ? JSON.stringify(targetSchema, null, 2)
      : "{}";
    const templateCtx = {
      dot_notation_path: absolutePath,
      question_schema_json: schemaSnippet,
      is_sub_question: pathParts.length > 1,
    };
    this.systemInstructions = await this.templateService.renderTemplate(
      "question_agent_prompt",
      templateCtx,
    );
    // Debug logging object as requested
    try {
      const debugPayload = {
        path: absolutePath,
        schema: targetSchema,
        systemInstructions: this.systemInstructions,
      };
      // Single consolidated log line for easier filtering
      console.log("QP_DEBUG_SESSION", debugPayload);
    } catch (e) {
      console.warn("Failed to log QP debug payload", e);
    }
    // Add hidden message to instruct the agent
    this.chatMessages.push({
      role: "user",
      content: `Introduce and ask ${absolutePath || "the root question"}.`,
      hidden: true,
    });
    this.developerMessageSent = true;
    this.sendingChat = true;
    try {
      const reply = await this.genAi.generate(
        this.chatMessages,
        this.systemInstructions,
      );
      this.chatMessages.push({ role: "model", content: reply });
    } finally {
      this.sendingChat = false;
    }
  }

  ngOnInit() {
    this.loadRemoteSchema();
  }

  onEditorInit(editor: any) {
    this.editorRef = editor;
  }

  formatSchema() {
    if (this.editorRef) {
      const action = this.editorRef.getAction?.("editor.action.formatDocument");
      if (action) {
        action.run();
        return;
      }
    }
    // Fallback manual pretty-print
    try {
      const parsed = JSON.parse(this.schemaJson);
      this.schemaJson = JSON.stringify(parsed, null, 2);
    } catch (e) {
      console.warn("Invalid JSON - cannot format", e);
    }
  }

  async loadRemoteSchema() {
    this.loadingSchema = true;
    this.loadSchemaError = "";
    try {
      const schema = await this.schemaService.getSchema();
      this.fullSchemaObj = schema;
      this.schemaJson = JSON.stringify(schema, null, 2);
      this.rebuildSchemaTree();
      this.buildPropertyPaths();
    } catch (e: any) {
      this.loadSchemaError = e.message || String(e);
    } finally {
      this.loadingSchema = false;
    }
  }

  private buildPropertyPaths() {
    this.propertyPaths = [];
    if (!this.fullSchemaObj) return;
    const seen = new Set<string>();
    const visit = (node: any, currentPath: string) => {
      if (!node || typeof node !== "object") return;
      // Object properties
      if (node.properties && typeof node.properties === "object") {
        for (const key of Object.keys(node.properties)) {
          const nextPath = currentPath ? `${currentPath}.${key}` : key;
          if (!seen.has(nextPath)) {
            this.propertyPaths.push(nextPath);
            seen.add(nextPath);
          }
          visit(node.properties[key], nextPath);
        }
      }
      // patternProperties (include patterns as paths)
      if (
        node.patternProperties &&
        typeof node.patternProperties === "object"
      ) {
        for (const key of Object.keys(node.patternProperties)) {
          const nextPath = currentPath ? `${currentPath}.${key}` : key;
          if (!seen.has(nextPath)) {
            this.propertyPaths.push(nextPath);
            seen.add(nextPath);
          }
          visit(node.patternProperties[key], nextPath);
        }
      }
      // Arrays: dive into items if object schema
      if (node.type === "array" && node.items) {
        const itemsSchema = node.items;
        if (!Array.isArray(itemsSchema) && itemsSchema.properties) {
          for (const key of Object.keys(itemsSchema.properties)) {
            const nextPath = currentPath ? `${currentPath}.${key}` : key;
            if (!seen.has(nextPath)) {
              this.propertyPaths.push(nextPath);
              seen.add(nextPath);
            }
            visit(itemsSchema.properties[key], nextPath);
          }
        }
      }
      // Combiners (shallow merge of properties)
      for (const combiner of ["allOf", "anyOf", "oneOf"] as const) {
        const list = node[combiner];
        if (Array.isArray(list)) {
          list.forEach((sub) => {
            if (sub && sub.properties) {
              for (const key of Object.keys(sub.properties)) {
                const nextPath = currentPath ? `${currentPath}.${key}` : key;
                if (!seen.has(nextPath)) {
                  this.propertyPaths.push(nextPath);
                  seen.add(nextPath);
                }
                visit(sub.properties[key], nextPath);
              }
            }
          });
        }
      }
    };
    // Kick off traversal from root
    visit(this.fullSchemaObj, "");
    this.propertyPaths.sort((a, b) => a.localeCompare(b));
  }

  private rebuildSchemaTree() {
    if (!this.fullSchemaObj || typeof this.fullSchemaObj !== "object") {
      this.schemaTree = [];
      return;
    }
    try {
      const root = this.buildNode(this.fullSchemaObj, "__root__");
      this.schemaTree = root.children || [];
    } catch (e) {
      console.warn("Failed to build schema tree", e);
      this.schemaTree = [];
    }
  }

  private buildNode(
    schema: any,
    name: string,
    parentPath: string = "",
  ): SchemaTreeNode {
    const currentPath = parentPath
      ? name === "__root__"
        ? parentPath
        : `${parentPath}.${name}`
      : name === "__root__"
        ? ""
        : name;
    const children: SchemaTreeNode[] = [];
    if (schema && typeof schema === "object") {
      // Prefer explicit properties if object
      if (schema.properties && typeof schema.properties === "object") {
        for (const key of Object.keys(schema.properties)) {
          children.push(
            this.buildNode(schema.properties[key], key, currentPath),
          );
        }
      }
      // Handle patternProperties as additional branches
      if (
        schema.patternProperties &&
        typeof schema.patternProperties === "object"
      ) {
        for (const key of Object.keys(schema.patternProperties)) {
          children.push(
            this.buildNode(schema.patternProperties[key], key, currentPath),
          );
        }
      }
      // If array, inspect items
      if (schema.type === "array" && schema.items) {
        const itemsSchema = schema.items;
        if (Array.isArray(itemsSchema)) {
          // tuple validation; enumerate object item schemas
          itemsSchema.forEach((itm) => {
            if (itm && typeof itm === "object") {
              children.push(
                this.buildNode(itm, `[${children.length}]`, currentPath),
              );
            }
          });
        } else if (itemsSchema && typeof itemsSchema === "object") {
          // items is single schema; if object or has properties
          if (itemsSchema.properties) {
            for (const key of Object.keys(itemsSchema.properties)) {
              children.push(
                this.buildNode(itemsSchema.properties[key], key, currentPath),
              );
            }
          } else if (itemsSchema.type === "object") {
            // object but maybe no properties yet
            // still attach placeholder children if later resolved (keep empty set)
          }
        }
      }
      // Support allOf/anyOf/oneOf merging simple object branches (shallow)
      for (const combiner of ["allOf", "anyOf", "oneOf"] as const) {
        const list = schema[combiner];
        if (Array.isArray(list)) {
          list.forEach((sub) => {
            if (sub && sub.properties) {
              for (const key of Object.keys(sub.properties)) {
                children.push(
                  this.buildNode(sub.properties[key], key, currentPath),
                );
              }
            }
          });
        }
      }
    }
    return { name, schema, children, path: currentPath };
  }

  // When clicking a node, load that portion of the schema into the editor
  selectNode(node: SchemaTreeNode, event: MouseEvent) {
    event.stopPropagation();
    if (!node?.schema) {
      return;
    }
    this.selectedNode = node; // track selection
    this.schemaJson = JSON.stringify(node.schema, null, 2);
    // Rebuild relative property paths for autocomplete
    this.propertyPaths = this.buildPropertyPathsFor(node.schema);
    this.pathQuery = "";
    this.pathValid = false;
    this.pathError = "";
    setTimeout(() => this.formatSchema(), 0);
  }

  // Optional: restore full schema
  restoreFullSchema() {
    if (this.fullSchemaObj) {
      this.selectedNode = null;
      this.schemaJson = JSON.stringify(this.fullSchemaObj, null, 2);
      this.propertyPaths = this.buildPropertyPathsFor(this.fullSchemaObj);
      this.pathQuery = "";
      this.pathValid = false;
      this.pathError = "";
      setTimeout(() => this.formatSchema(), 0);
    }
  }

  get visibleChatMessages(): ChatMessage[] {
    return this.chatMessages.filter((m) => !m.hidden);
  }

  // Removed blur handler and internal method; replaced with on-change validation
  validatePathOnChange() {
    const raw = this.pathQuery.trim();
    if (!raw) {
      this.pathValid = false;
      this.pathError = "";
      return;
    }
    if (raw === this.TOP_OPTION) {
      this.pathValid = true;
      this.pathError = "";
      return;
    }
    const root = this.selectedNode
      ? this.selectedNode.schema
      : this.fullSchemaObj;
    const resolved = this.getSchemaAtRelativePath(raw, root);
    if (resolved) {
      this.pathValid = true;
      this.pathError = "";
    } else {
      this.pathValid = false;
      this.pathError = "Invalid path";
    }
  }

  // Autocomplete handlers
  onPathInput(evt: Event) {
    this.pathQuery = (evt.target as HTMLInputElement).value;
    this.updatePathSuggestions();
    this.validatePathOnChange();
  }

  onPathFocus() {
    // If empty, show top-level suggestions immediately (include <Top> option)
    if (!this.pathQuery.trim()) {
      this.filteredPaths = [
        this.TOP_OPTION,
        ...this.propertyPaths.slice(0, 50),
      ];
      this.activeSuggestionIndex = -1;
      this.showPathSuggestions = this.filteredPaths.length > 0;
    } else {
      this.updatePathSuggestions(true);
    }
  }

  onPathBlur() {
    setTimeout(() => {
      this.showPathSuggestions = false;
    }, 120);
  }

  onPathKeyDown(evt: KeyboardEvent) {
    if (!this.showPathSuggestions || !this.filteredPaths.length) return;
    if (evt.key === "ArrowDown") {
      evt.preventDefault();
      this.activeSuggestionIndex =
        (this.activeSuggestionIndex + 1) % this.filteredPaths.length;
    } else if (evt.key === "ArrowUp") {
      evt.preventDefault();
      this.activeSuggestionIndex =
        (this.activeSuggestionIndex - 1 + this.filteredPaths.length) %
        this.filteredPaths.length;
    } else if (evt.key === "Enter") {
      if (
        this.activeSuggestionIndex >= 0 &&
        this.activeSuggestionIndex < this.filteredPaths.length
      ) {
        evt.preventDefault();
        this.applySuggestion(this.filteredPaths[this.activeSuggestionIndex]);
      }
    } else if (evt.key === "Escape") {
      this.showPathSuggestions = false;
      this.activeSuggestionIndex = -1;
    } else if (evt.key === "Tab") {
      if (
        this.activeSuggestionIndex >= 0 &&
        this.activeSuggestionIndex < this.filteredPaths.length
      ) {
        this.applySuggestion(this.filteredPaths[this.activeSuggestionIndex]);
      }
    }
  }

  applySuggestion(s: string) {
    this.pathQuery = s;
    this.showPathSuggestions = false;
    this.activeSuggestionIndex = -1;
    this.validatePathOnChange();
  }

  updatePathSuggestions(force = false) {
    const q = this.pathQuery.trim();
    if (!q) {
      if (force) {
        this.filteredPaths = [
          this.TOP_OPTION,
          ...this.propertyPaths.slice(0, 50),
        ];
        this.showPathSuggestions = this.filteredPaths.length > 0;
      } else {
        this.filteredPaths = [];
        this.showPathSuggestions = false;
      }
      this.activeSuggestionIndex = -1;
      return;
    }
    const lower = q.toLowerCase();
    const matches = this.propertyPaths.filter((p) =>
      p.toLowerCase().startsWith(lower),
    );
    // Include <Top> when user types beginning of it
    if ("<top>".startsWith(lower)) {
      this.filteredPaths = [this.TOP_OPTION, ...matches.slice(0, 49)];
    } else {
      this.filteredPaths = matches.slice(0, 50);
    }
    this.showPathSuggestions =
      (force ||
        document.activeElement ===
          (document.querySelector(".path-input") as HTMLElement)) &&
      this.filteredPaths.length > 0;
    if (this.activeSuggestionIndex >= this.filteredPaths.length)
      this.activeSuggestionIndex = -1;
  }

  private getSchemaAtRelativePath(path: string, root: any): any {
    if (!root) return null;
    const segments = path
      .replace(/\[(\d+)]/g, ".$1")
      .split(".")
      .filter((s) => s.length);
    let current: any = root;
    for (const seg of segments) {
      if (current == null) return null;
      if (/^\d+$/.test(seg)) {
        const idx = Number(seg);
        if (Array.isArray(current)) current = current[idx];
        else if (current.type === "array" && current.items) {
          if (Array.isArray(current.items)) current = current.items[idx];
          else current = current.items;
        } else return null;
      } else {
        let next: any = null;
        if (current.properties && current.properties[seg])
          next = current.properties[seg];
        else if (current.patternProperties && current.patternProperties[seg])
          next = current.patternProperties[seg];
        else if (current.type === "array" && current.items) {
          const items = current.items;
          if (
            !Array.isArray(items) &&
            items.properties &&
            items.properties[seg]
          )
            next = items.properties[seg];
        }
        if (!next) {
          for (const combiner of ["allOf", "anyOf", "oneOf"] as const) {
            const list = current[combiner];
            if (Array.isArray(list)) {
              for (const sub of list) {
                if (sub && sub.properties && sub.properties[seg]) {
                  next = sub.properties[seg];
                  break;
                }
                if (
                  sub &&
                  sub.patternProperties &&
                  sub.patternProperties[seg]
                ) {
                  next = sub.patternProperties[seg];
                  break;
                }
              }
              if (next) break;
            }
          }
        }
        if (!next) return null;
        current = next;
      }
    }
    return current;
  }

  async sendChat() {
    const text = this.chatInput.trim();
    if (!text || this.sendingChat) return;
    this.chatMessages.push({ role: "user", content: text });
    this.chatInput = "";
    this.focusChatInput();
    this.sendingChat = true;
    try {
      const reply = await this.genAi.generate(
        this.chatMessages,
        this.systemInstructions,
      );
      this.chatMessages.push({ role: "model", content: reply });
    } finally {
      this.sendingChat = false;
      this.focusChatInput();
    }
  }

  toggleSystemInstructions() {
    this.showSystemInstructions = !this.showSystemInstructions;
  }

  private buildPropertyPathsFor(root: any): string[] {
    const results: string[] = [];
    if (!root || typeof root !== "object") return results;
    const seen = new Set<string>();
    const visit = (node: any, prefix: string) => {
      if (!node || typeof node !== "object") return;
      if (node.properties && typeof node.properties === "object") {
        for (const key of Object.keys(node.properties)) {
          const path = prefix ? `${prefix}.${key}` : key;
          if (!seen.has(path)) {
            results.push(path);
            seen.add(path);
          }
          visit(node.properties[key], path);
        }
      }
      if (
        node.patternProperties &&
        typeof node.patternProperties === "object"
      ) {
        for (const key of Object.keys(node.patternProperties)) {
          const path = prefix ? `${prefix}.${key}` : key;
          if (!seen.has(path)) {
            results.push(path);
            seen.add(path);
          }
          visit(node.patternProperties[key], path);
        }
      }
      if (node.type === "array" && node.items) {
        const itemsSchema = node.items;
        if (!Array.isArray(itemsSchema) && itemsSchema.properties) {
          for (const key of Object.keys(itemsSchema.properties)) {
            const path = prefix ? `${prefix}.${key}` : key;
            if (!seen.has(path)) {
              results.push(path);
              seen.add(path);
            }
            visit(itemsSchema.properties[key], path);
          }
        }
      }
      for (const combiner of ["allOf", "anyOf", "oneOf"] as const) {
        const list = node[combiner];
        if (Array.isArray(list)) {
          list.forEach((sub) => {
            if (sub && sub.properties) {
              for (const key of Object.keys(sub.properties)) {
                const path = prefix ? `${prefix}.${key}` : key;
                if (!seen.has(path)) {
                  results.push(path);
                  seen.add(path);
                }
                visit(sub.properties[key], path);
              }
            }
          });
        }
      }
    };
    visit(root, "");
    results.sort((a, b) => a.localeCompare(b));
    return results;
  }
}

interface SchemaTreeNode {
  name: string;
  schema: any;
  children?: SchemaTreeNode[];
  path: string; // full dot notation path from root (empty for root)
}
